import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import Navigation from './components/Navigation';
import StickyCartBar from './components/StickyCartBar';
import QRCodeTracker from './components/QRCodeTracker';
import StripeTestBanner from './components/StripeTestBanner';
import TawkChat from './components/TawkChat';
import SessionExpiryModal, { useSessionExpiryWarning } from './components/SessionExpiryModal';
import { useSessionFreshness } from './hooks/usePageVisibility';
import { useLanguageProfileSync } from './hooks/useLanguageProfileSync';
import AuthAwareErrorBoundary from './components/AuthAwareErrorBoundary';
import * as Sentry from '@sentry/react';

// Auto-reload on stale chunk errors (after deploys, old chunk hashes 404)
function lazyWithRetry(importFn: () => Promise<any>) {
  return React.lazy(() =>
    importFn().catch((error) => {
      // Only reload once per session to prevent infinite loops
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      throw error; // already reloaded once, let error boundary handle it
    })
  );
}

// Dashboard Layout
const DashboardLayout = lazyWithRetry(() => import('./layouts/DashboardLayout'));

// Lazy load components - Customer pages
const Home = lazyWithRetry(() => import('./pages/Home'));
const ComingSoon = lazyWithRetry(() => import('./pages/ComingSoon'));
const SignIn = lazyWithRetry(() => import('./pages/SignIn'));
const SignUp = lazyWithRetry(() => import('./pages/SignUp'));
const Venues = lazyWithRetry(() => import('./pages/Venues'));
const VenueDetails = lazyWithRetry(() => import('./pages/VenueDetails'));
const VenueMenu = lazyWithRetry(() => import('./pages/VenueMenu'));
const Checkout = lazyWithRetry(() => import('./pages/Checkout'));
const OrderConfirmation = lazyWithRetry(() => import('./pages/OrderConfirmation'));
const FavoriteVenues = lazyWithRetry(() => import('./pages/FavoriteVenues'));
const FavoriteOrders = lazyWithRetry(() => import('./pages/FavoriteOrders'));
const OrderHistory = lazyWithRetry(() => import('./pages/OrderHistory'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const LoyaltyDashboard = lazyWithRetry(() => import('./pages/LoyaltyDashboard'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));

// Business Portal Pages
const BusinessDashboard = lazyWithRetry(() => import('./pages/BusinessDashboard'));
const CreateBusiness = lazyWithRetry(() => import('./pages/CreateBusiness'));
const BusinessDetail = lazyWithRetry(() => import('./pages/BusinessDetail'));
const CreateVenue = lazyWithRetry(() => import('./pages/CreateVenue'));
const VenueDetail = lazyWithRetry(() => import('./pages/VenueDetail'));
const EditVenue = lazyWithRetry(() => import('./pages/EditVenue'));
const EditVenueHours = lazyWithRetry(() => import('./pages/EditVenueHours'));
const MenuItems = lazyWithRetry(() => import('./pages/MenuItems'));
const CreateMenuItem = lazyWithRetry(() => import('./pages/CreateMenuItem'));
const EditMenuItem = lazyWithRetry(() => import('./pages/EditMenuItem'));
const AnalyticsDashboard = lazyWithRetry(() => import('./pages/AnalyticsDashboard'));
const DiscountCodes = lazyWithRetry(() => import('./pages/DiscountCodes'));

// Staff Management Pages
const StaffManagement = lazyWithRetry(() => import('./pages/StaffManagement'));
const StaffDetail = lazyWithRetry(() => import('./pages/StaffDetail'));
const InviteStaff = lazyWithRetry(() => import('./pages/InviteStaff'));
const StaffInvitationAccept = lazyWithRetry(() => import('./pages/StaffInvitationAccept'));
const VenueStaffPage = lazyWithRetry(() => import('./pages/VenueStaffPage'));

// Staff Pages
const MyAssignments = lazyWithRetry(() => import('./pages/MyAssignments'));
const MyPerformance = lazyWithRetry(() => import('./pages/MyPerformance'));

// Order Management
const OrderManagement = lazyWithRetry(() => import('./pages/OrderManagement'));

// Protected route component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-dash-bg-base flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-dash-accent animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}

function App() {
  const { loading, user } = useAuth();
  const location = useLocation();

  // Clear chunk reload flag on successful load (allows retry on next deploy)
  React.useEffect(() => {
    sessionStorage.removeItem('chunk_reload');
  }, []);

  // Use session management hooks
  useSessionFreshness();
  useLanguageProfileSync();
  const { showWarning, expiresAt, handleExtend, handleSignOut } = useSessionExpiryWarning();

  // Determine if we're on a business dashboard route
  const isBusinessRoute = location.pathname.startsWith('/business') || location.pathname === '/orders';

  // Only show cart on venue-specific pages and not on checkout or confirmation
  const showCart = location.pathname.startsWith('/venues/') &&
    !location.pathname.includes('/checkout') &&
    !location.pathname.includes('/confirmation');

  if (loading) {
    return (
      <div className="min-h-screen bg-dash-bg-base flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-dash-accent animate-spin" />
      </div>
    );
  }

  return (
    <>
      <StripeTestBanner />
      {/* Only show top nav on customer-facing routes */}
      {!isBusinessRoute && location.pathname !== '/soon' && <Navigation />}
      {/* Live chat loads on customer pages only; launcher stays hidden until
          opened via "Contact Support". Never loads on business/staff routes. */}
      <TawkChat enabled={!isBusinessRoute && location.pathname !== '/soon'} />
      <QRCodeTracker />

      {/* Session Expiry Warning Modal */}
      {user && showWarning && (
        <SessionExpiryModal
          expiresAt={expiresAt}
          onExtend={handleExtend}
          onSignOut={handleSignOut}
        />
      )}
      <React.Suspense
        fallback={
          <div className="min-h-screen bg-dash-bg-base flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-dash-accent animate-spin" />
          </div>
        }
      >
        <Routes>
          {/* ── Customer Routes ── */}
          <Route path="/" element={<Home />} />
          <Route path="/soon" element={<ComingSoon />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/venues" element={<Venues />} />
          <Route path="/venues/:id" element={<VenueDetails />} />
          <Route path="/venues/:id/menu" element={<VenueMenu />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/confirmation/:id" element={<OrderConfirmation />} />

          <Route path="/favorites/venues" element={<ProtectedRoute><FavoriteVenues /></ProtectedRoute>} />
          <Route path="/favorites/orders" element={<ProtectedRoute><FavoriteOrders /></ProtectedRoute>} />
          <Route path="/order-history" element={<ProtectedRoute><OrderHistory /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/loyalty" element={<ProtectedRoute><LoyaltyDashboard /></ProtectedRoute>} />

          {/* Public staff invitation route */}
          <Route path="/staff-invitation/:token" element={<StaffInvitationAccept />} />

          {/* ── Business Dashboard Routes (wrapped in DashboardLayout with sidebar) ── */}
          <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route path="/business" element={<BusinessDashboard />} />
            <Route path="/business/create" element={<CreateBusiness />} />
            <Route path="/business/:businessId" element={<BusinessDetail />} />
            <Route path="/business/:businessId/my-assignments" element={<MyAssignments />} />
            <Route path="/business/:businessId/my-performance" element={<MyPerformance />} />

            {/* Staff Management */}
            <Route path="/business/:businessId/staff" element={<StaffManagement />} />
            <Route path="/business/:businessId/staff/:staffId" element={<StaffDetail />} />
            <Route path="/business/:businessId/staff/invite" element={<InviteStaff />} />

            {/* Venue Management */}
            <Route path="/business/:businessId/venues/create" element={<CreateVenue />} />
            <Route path="/business/:businessId/venues/:venueId" element={<VenueDetail />} />
            <Route path="/business/:businessId/venues/:venueId/edit" element={<EditVenue />} />
            <Route path="/business/:businessId/venues/:venueId/hours/edit" element={<EditVenueHours />} />
            <Route path="/business/:businessId/venues/:venueId/staff" element={<VenueStaffPage />} />

            {/* Menu Management */}
            <Route path="/business/:businessId/venues/:venueId/menu" element={<MenuItems />} />
            <Route path="/business/:businessId/venues/:venueId/menu/create" element={<CreateMenuItem />} />
            <Route path="/business/:businessId/venues/:venueId/menu/:menuItemId/edit" element={<EditMenuItem />} />

            {/* Analytics */}
            <Route path="/business/:businessId/analytics" element={<AnalyticsDashboard />} />

            {/* Discount Codes */}
            <Route path="/business/:businessId/discounts" element={<DiscountCodes />} />

            {/* Orders */}
            <Route path="/orders" element={<OrderManagement />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </React.Suspense>
      {showCart && <StickyCartBar />}
    </>
  );
}

// Wrap App with auth-aware error boundary
function AppWithErrorBoundary() {
  return (
    <AuthAwareErrorBoundary>
      <App />
    </AuthAwareErrorBoundary>
  );
}

// Then wrap with Sentry for additional error tracking
const SentryApp = Sentry.withProfiler(AppWithErrorBoundary);

export default SentryApp;
